import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

const OWNERS = ['All', 'Ben', 'Spencer', 'Spencer\\Ben'];
const FOIL_OPTIONS = [
  { value: '',     label: 'Any' },
  { value: 'Yes',  label: 'Foil' },
  { value: 'No',   label: 'Non-foil' },
];
const STATUS_OPTIONS = [
  ['all',    'All'],
  ['active', 'Active'],
  ['sold',   'Sold'],
];
const SORT_OPTIONS = [
  ['name',         'Name (A→Z)'],
  ['name_desc',    'Name (Z→A)'],
  ['value_desc',   'Price (high→low)'],
  ['value_asc',    'Price (low→high)'],
  ['listed_desc',  'Newest listed'],
  ['listed_asc',   'Oldest listed'],
  ['sold_desc',    'Most recently sold'],
];
const PAGE_SIZE = 50;

const fmtMoney = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return `$${v.toFixed(2)}`;
};

const fmtAgo = (ts) => {
  if (!ts) return 'never';
  const secs = Math.max(0, Math.floor(Date.now() / 1000) - ts);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

export default function Inventory({ onClose }) {
  const { authFetch } = useAuth();

  // ── Filter state ──
  const [q,         setQ]         = useState('');
  const [owner,     setOwner]     = useState('All');
  const [setCode,   setSetCode]   = useState('');
  const [foil,      setFoil]      = useState('');
  const [status,    setStatus]    = useState('all');
  const [minPrice,  setMinPrice]  = useState('');
  const [maxPrice,  setMaxPrice]  = useState('');
  const [sort,      setSort]      = useState('name');
  const [page,      setPage]      = useState(0);

  // ── Server state ──
  const [results, setResults] = useState([]);
  const [total,   setTotal]   = useState(0);
  const [syncStatus, setSyncStatus] = useState({ last_sync_ts: 0, row_count: 0 });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Re-render the "X ago" label every 15s so it stays honest without a refetch.
  const [, forceRerender] = useState(0);
  useEffect(() => {
    const t = setInterval(() => forceRerender(n => n + 1), 15000);
    return () => clearInterval(t);
  }, []);

  // Debounce the q input so we don't hammer the API on every keystroke. Other
  // filters update immediately because they change at human-click speed.
  const [debouncedQ, setDebouncedQ] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to page 0 whenever a filter changes so the user doesn't get stuck on
  // page 5 of a query that only has 1 page.
  useEffect(() => {
    setPage(0);
  }, [debouncedQ, owner, setCode, foil, status, minPrice, maxPrice, sort]);

  const lastReqIdRef = useRef(0);
  const fetchResults = useCallback(async () => {
    const reqId = ++lastReqIdRef.current;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.set('q', debouncedQ);
      if (owner && owner !== 'All') params.set('owner', owner);
      if (setCode) params.set('set', setCode);
      if (foil) params.set('foil', foil);
      if (status && status !== 'all') params.set('status', status);
      if (minPrice !== '') params.set('min_price', minPrice);
      if (maxPrice !== '') params.set('max_price', maxPrice);
      params.set('sort', sort);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));

      const res = await authFetch(`/api/inventory/search?${params.toString()}`);
      const data = await res.json();
      // Drop stale responses if the user fired another query while this one
      // was in flight.
      if (reqId !== lastReqIdRef.current) return;
      if (!res.ok) throw new Error(data.error || 'Search failed');
      setResults(data.results || []);
      setTotal(data.total || 0);
      if (data.status) setSyncStatus(data.status);
    } catch (e) {
      if (reqId === lastReqIdRef.current) setError(e.message);
    } finally {
      if (reqId === lastReqIdRef.current) setLoading(false);
    }
  }, [authFetch, debouncedQ, owner, setCode, foil, status, minPrice, maxPrice, sort, page]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const refresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      const res = await authFetch('/api/inventory/refresh', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Refresh failed');
      if (data.status) setSyncStatus(data.status);
      // Re-run the active query so the UI shows the freshest rows.
      await fetchResults();
    } catch (e) {
      setError(e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo   = Math.min(total, (page + 1) * PAGE_SIZE);

  const activeFilterCount = useMemo(() => (
    (debouncedQ ? 1 : 0) +
    (owner !== 'All' ? 1 : 0) +
    (setCode ? 1 : 0) +
    (foil ? 1 : 0) +
    (status !== 'all' ? 1 : 0) +
    (minPrice !== '' ? 1 : 0) +
    (maxPrice !== '' ? 1 : 0)
  ), [debouncedQ, owner, setCode, foil, status, minPrice, maxPrice]);

  const clearAll = () => {
    setQ(''); setOwner('All'); setSetCode(''); setFoil('');
    setStatus('all'); setMinPrice(''); setMaxPrice(''); setSort('name');
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div
        className="admin-panel admin-panel-wide"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 1200 }}
      >
        <div className="admin-header">
          <div className="admin-title">🔍 Inventory Search</div>
          <button className="admin-close" onClick={onClose}>✕</button>
        </div>

        <div className="analytics" style={{ paddingTop: 12 }}>
          {error && <div className="admin-error">{error}</div>}

          {/* Filter bar */}
          <div className="analytics-controls">
            <div className="ctl-group" style={{ flex: '1 1 240px', minWidth: 200 }}>
              <label className="ctl-label">Name</label>
              <input
                type="text"
                className="admin-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search card name…"
                autoFocus
              />
            </div>

            <div className="ctl-group">
              <label className="ctl-label">Owner</label>
              <select className="admin-select" value={owner} onChange={(e) => setOwner(e.target.value)}>
                {OWNERS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            <div className="ctl-group">
              <label className="ctl-label">Set</label>
              <input
                type="text"
                className="admin-input"
                value={setCode}
                onChange={(e) => setSetCode(e.target.value)}
                placeholder="e.g. LRW"
                style={{ width: 100, textTransform: 'uppercase' }}
              />
            </div>

            <div className="ctl-group">
              <label className="ctl-label">Foil</label>
              <select className="admin-select" value={foil} onChange={(e) => setFoil(e.target.value)}>
                {FOIL_OPTIONS.map(o => (
                  <option key={o.value || 'any'} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="ctl-group">
              <label className="ctl-label">Status</label>
              <div className="seg">
                {STATUS_OPTIONS.map(([v, l]) => (
                  <button
                    key={v}
                    className={`seg-btn ${status === v ? 'is-active' : ''}`}
                    onClick={() => setStatus(v)}
                  >{l}</button>
                ))}
              </div>
            </div>

            <div className="ctl-group">
              <label className="ctl-label">Min $</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="admin-input"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                style={{ width: 80 }}
              />
            </div>

            <div className="ctl-group">
              <label className="ctl-label">Max $</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="admin-input"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                style={{ width: 80 }}
              />
            </div>

            <div className="ctl-group">
              <label className="ctl-label">Sort</label>
              <select className="admin-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                {SORT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>

            {activeFilterCount > 0 && (
              <button
                className="admin-refresh"
                onClick={clearAll}
                title="Clear all filters"
                style={{ borderColor: '#a44', color: '#a44' }}
              >clear</button>
            )}

            <button
              className="admin-refresh"
              onClick={refresh}
              disabled={refreshing || loading}
              title="Pull the latest from Google Sheets now"
            >{refreshing ? '…' : '↻'}</button>
          </div>

          {/* Sync indicator */}
          <div style={{
            fontSize: 11, color: '#666', marginBottom: 10,
            display: 'flex', gap: 12, flexWrap: 'wrap',
          }}>
            <span>
              Last synced: <strong style={{ color: '#999' }}>{fmtAgo(syncStatus.last_sync_ts)}</strong>
              {' · '}
              <strong style={{ color: '#999' }}>{syncStatus.row_count.toLocaleString()}</strong> rows mirrored
            </span>
            <span style={{ flex: 1 }} />
            <span>
              {total > 0
                ? <>Showing <strong style={{ color: '#999' }}>{showingFrom}–{showingTo}</strong> of <strong style={{ color: '#999' }}>{total.toLocaleString()}</strong></>
                : (loading ? 'Searching…' : 'No matches')}
            </span>
          </div>

          {/* Results table */}
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Card</th>
                  <th>Set</th>
                  <th>#</th>
                  <th>Cond</th>
                  <th>Foil</th>
                  <th>Owner</th>
                  <th className="num">Price</th>
                  <th>Listed</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {results.length === 0 && !loading && (
                  <tr><td colSpan="10" className="admin-empty">No cards match these filters</td></tr>
                )}
                {results.map((r) => (
                  <tr key={r.row_index}>
                    <td>{r.name}</td>
                    <td className="muted">{r.set}</td>
                    <td className="muted">{r.number}</td>
                    <td className="muted">{r.condition}</td>
                    <td className="muted">{r.foil || '—'}</td>
                    <td>{r.owner}</td>
                    <td className="num strong">{fmtMoney(r.cur_value)}</td>
                    <td className="muted">{r.listed_iso || '—'}</td>
                    <td>
                      {r.is_sold
                        ? <span style={{ color: '#9cf' }}>
                            sold{r.sold_iso ? ` ${r.sold_iso}` : ''}
                          </span>
                        : <span style={{ color: '#9f9' }}>active</span>}
                    </td>
                    <td>
                      {r.url && (
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open on Scryfall"
                          style={{ color: '#888', textDecoration: 'none' }}
                        >↗</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12,
              padding: '12px 0 4px', fontSize: 12, color: '#888',
            }}>
              <button
                className="admin-refresh"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
              >← Prev</button>
              <span>Page <strong style={{ color: '#ccc' }}>{page + 1}</strong> of {totalPages}</span>
              <button
                className="admin-refresh"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
              >Next →</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
